import axios from "axios";
import { useEffect, useRef, useState } from "react";
import {
  FiBriefcase,
  FiCalendar,
  FiDownload,
  FiEye,
  FiSearch,
  FiX,
  FiUser,
  FiRefreshCw,
  FiMapPin,
  FiDollarSign,
  FiPhone,
  FiMail,
  FiFileText,
  FiClipboard
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "./EmployeeDashboard.css";

const API_BASE_URL = 'https://ingrainhirebackend.ingrainsystems.com/api';

const DetailItem = ({ icon, label, value }) => (
  <div className="emp-dash__detail-row">
    <span className="emp-dash__detail-label">{label}</span>
    <span className="emp-dash__detail-value">{value || "N/A"}</span>
  </div>
);

// Mobile Card Component
const EmployeeCard = ({ item, handleOpenModal }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-4 shadow-lg shadow-gray-100 hover:shadow-xl hover:shadow-blue-100/50 transition-all duration-300 group">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="font-bold text-gray-900 text-sm group-hover:text-blue-600 transition-colors">{item.candidate?.name || "Unknown"}</h3>
          <p className="text-[11px] text-gray-500 font-medium mt-0.5">{item.lastUpdated ? new Date(item.lastUpdated).toLocaleDateString() : "No updates"}</p>
        </div>
        <span className="px-3 py-1.5 rounded-full text-[11px] bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 font-semibold border border-emerald-200 shadow-sm">
          Verified
        </span>
      </div>

      {/* Role & Company */}
      <div className="mb-4">
        <span className="inline-block px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600 text-[11px] font-semibold rounded-full border border-blue-100 shadow-sm">
          {item.latestRole}
        </span>
        <p className="text-xs text-gray-600 mt-1.5 font-medium">{item.latestCompany}</p>
      </div>

      {/* Contact & Roles */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-3 rounded-xl border border-gray-100">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Contact</p>
          <p className="text-xs font-semibold text-gray-700 truncate">{item.candidate?.phone || "N/A"}</p>
        </div>
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-3 rounded-xl border border-indigo-100">
          <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-1">Roles Logged</p>
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 text-indigo-700 font-bold text-xs border border-indigo-200 shadow-sm">
            {item.experiences.length}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4">
        <button
          onClick={() => handleOpenModal(item)}
          className="w-full px-4 py-2.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white rounded-xl transition-all duration-300 border border-indigo-100 flex items-center justify-center gap-1.5 text-xs font-bold shadow-sm hover:shadow-md hover:scale-105"
        >
          <FiEye className="text-xs" /> View Details
        </button>
      </div>
    </div>
  );
};


const EmployeeJourney = () => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef(null);
  const [roles, setRoles] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Pagination states
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
  });

  const navigate = useNavigate();

  // Get clientId from localStorage
  const getClientId = () => {
    return localStorage.getItem('clientId') || localStorage.getItem('clientCustomId') || '';
  };

  useEffect(() => {
    const clientId = getClientId();
    if (!clientId) {
      alert("Please login first!");
      navigate("/login");
      return;
    }
    fetchExperiences();
    fetchRoles();

    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setIsRoleDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchRoles = async () => {
    try {
      const clientId = getClientId();
      const res = await axios.get(`${API_BASE_URL}/roles/all?clientId=${clientId}`);
      if (res.data.success) {
        setRoles(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
  };

  // ✅ FIXED: Fetch experiences with clientId
  const fetchExperiences = async () => {
    try {
      setLoading(true);
      setError("");
      
      const clientId = getClientId();
      if (!clientId) {
        setError("Client ID not found. Please login again.");
        setLoading(false);
        return;
      }

      // 🔥 API call with clientId in params
      const res = await axios.get(`${API_BASE_URL}/candidate/all-experiences/${clientId}`);
      
      if (res.data.success) {
        setExperiences(res.data.data);
      } else {
        setError(res.data.message || "Failed to fetch journey data");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch journey data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Grouping logic
  const groupedData = experiences.reduce((acc, exp) => {
    const candidateId = exp.candidateId?._id || exp.candidateId || "unknown";
    if (!acc[candidateId]) {
      acc[candidateId] = {
        candidate: exp.candidateId,
        experiences: [],
        latestRole: "",
        latestCompany: ""
      };
    }
    acc[candidateId].experiences.push(exp);
    return acc;
  }, {});

  // Sort experiences for each candidate and pick latest info
  const candidates = Object.values(groupedData).map(item => {
    const sortedExp = [...item.experiences].sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    return {
      ...item,
      experiences: sortedExp,
      latestRole: sortedExp[0]?.role || "Unknown",
      latestCompany: sortedExp[0]?.companyName || "Unknown",
      lastUpdated: sortedExp[0]?.createdAt || null
    };
  });

  const filteredCandidates = candidates.filter(item => {
    const query = searchQuery.toLowerCase();
    const candidate = item.candidate || {};
    const matchesSearch =
      (candidate.name || "").toLowerCase().includes(query) ||
      (candidate.email || "").toLowerCase().includes(query) ||
      (item.latestRole || "").toLowerCase().includes(query);

    const matchesRole = roleFilter ? (item.latestRole === roleFilter) : true;

    let matchesDate = true;
    if (dateFilter) {
      const updateDate = item.lastUpdated ? new Date(item.lastUpdated).toISOString().split('T')[0] : "";
      matchesDate = updateDate === dateFilter;
    }

    return matchesSearch && matchesRole && matchesDate;
  });

  // Pagination Handlers
  const handleItemsPerPageChange = (limit) => {
    setPagination({
      currentPage: 1,
      limit: limit,
      totalCount: filteredCandidates.length,
      totalPages: Math.ceil(filteredCandidates.length / limit)
    });
  };

  const handlePrevPage = () => {
    if (pagination.currentPage > 1) {
      setPagination(prev => ({
        ...prev,
        currentPage: prev.currentPage - 1
      }));
    }
  };

  const handleNextPage = () => {
    if (pagination.currentPage < pagination.totalPages) {
      setPagination(prev => ({
        ...prev,
        currentPage: prev.currentPage + 1
      }));
    }
  };

  const handlePageClick = (page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    for (let i = 1; i <= pagination.totalPages; i++) {
      if (
        i === 1 ||
        i === pagination.totalPages ||
        (i >= pagination.currentPage - 2 && i <= pagination.currentPage + 2)
      ) {
        pageNumbers.push(i);
      } else if (i === pagination.currentPage - 3 || i === pagination.currentPage + 3) {
        pageNumbers.push("...");
      }
    }
    return pageNumbers;
  };

  // Update pagination when filtered results change
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      totalCount: filteredCandidates.length,
      totalPages: Math.ceil(filteredCandidates.length / prev.limit),
      currentPage: 1
    }));
  }, [filteredCandidates.length, searchQuery, roleFilter, dateFilter]);

  // Calculate pagination
  const indexOfLastItem = pagination.currentPage * pagination.limit;
  const indexOfFirstItem = indexOfLastItem - pagination.limit;
  const currentItems = filteredCandidates.slice(indexOfFirstItem, indexOfLastItem);

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const handleOpenModal = (candidateItem) => {
    setSelectedCandidate(candidateItem);
    setIsModalOpen(true);
  };

  const resetFilters = () => {
    setSearchQuery("");
    setRoleFilter("");
    setDateFilter("");
    setRoleSearchQuery("");
  };

  // Function to get file URL
  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    if (filePath.startsWith('http')) return filePath;
    return `${API_BASE_URL}/${filePath.replace(/\\/g, '/')}`;
  };

  return (
    <div style={{backgroundColor:'white'}} className="emp-dash">
      <main className="">
        {/* Dashboard Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Employee <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Journey</span>
            </h1>
            <p className="emp-dash__subtitle">
              View and manage employee professional history
            </p>
          </div>
          <div className="emp-dash__date-pill bg-gradient-to-r from-indigo-50 via-blue-50 to-purple-50 border-indigo-200 shadow-lg shadow-indigo-100">
            <FiClipboard className="text-indigo-600" />
            <span className="text-indigo-700 font-semibold">
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Filters Section */}
        <div className="emp-dash__card mb-6 shadow-lg shadow-gray-100">
          <div className="emp-dash__card-header bg-gradient-to-r from-slate-50 to-blue-50">
            <div>
              <h3 className="emp-dash__card-title flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md">
                  <FiSearch size={14} />
                </div>
                Filter Employees
              </h3>
              <p className="emp-dash__card-desc">Search by name, email, role, or date</p>
            </div>
          </div>
          <div className="emp-dash__card-body bg-gradient-to-br from-white via-gray-50 to-blue-50/20">
            <div className="flex flex-wrap items-center gap-3">

          {/* Search Bar */}
          <div className="relative flex-1 min-w-[200px]">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search name, email, role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white shadow-sm hover:shadow-md transition-shadow"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors"
              >
                <FiX size={12} />
              </button>
            )}
          </div>

          {/* Role Filter Button */}
          <div className="relative" ref={roleDropdownRef}>
            <button
              onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
              className={`h-10 px-4 py-2.5 text-sm font-medium rounded-xl transition flex items-center gap-2 shadow-sm hover:shadow-md ${
                roleFilter 
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-100' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
              }`}
            >
              <FiBriefcase className={roleFilter ? "text-blue-100" : "text-gray-400"} size={14} /> Role {roleFilter && `: ${roleFilter}`}
              <span className={`text-gray-400 transition-transform ${isRoleDropdownOpen ? 'rotate-180' : ''}`}>▾</span>
            </button>
            
            {/* Role Filter Dropdown */}
            {isRoleDropdownOpen && (
              <div className="absolute z-50 mt-2 w-56 bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-72 overflow-y-auto">
                <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-blue-50 sticky top-0">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                    <input
                      type="text"
                      className="w-full py-2.5 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm"
                      placeholder="Search roles..."
                      value={roleSearchQuery}
                      onChange={(e) => setRoleSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                </div>
                <div 
                  onClick={() => {
                    setRoleFilter('');
                    setIsRoleDropdownOpen(false);
                    setRoleSearchQuery('');
                  }}
                  className={`px-4 py-3 text-sm hover:bg-blue-50 cursor-pointer border-b border-gray-100 font-medium transition-colors ${
                    !roleFilter ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  All Roles
                </div>
                {roles
                  .filter(r => r.name?.toLowerCase().includes(roleSearchQuery.toLowerCase()))
                  .map((r) => (
                    <div 
                      key={r._id}
                      onClick={() => {
                        setRoleFilter(r.name);
                        setIsRoleDropdownOpen(false);
                        setRoleSearchQuery('');
                      }}
                      className={`px-4 py-3 text-sm hover:bg-blue-50 cursor-pointer transition-colors ${
                        roleFilter === r.name ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                      }`}
                    >
                      {r.name}
                    </div>
                  ))}
                {roles.filter(r => r.name?.toLowerCase().includes(roleSearchQuery.toLowerCase())).length === 0 && (
                  <div className="px-4 py-6 text-sm text-gray-400 text-center">
                    No roles found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Date Filter */}
          <div className="relative">
            <button
              onClick={(e) => {
                const dateInput = e.currentTarget.nextElementSibling;
                if (dateInput && dateInput.showPicker) {
                  dateInput.showPicker();
                } else {
                  dateInput?.focus();
                }
              }}
              className={`h-10 px-4 py-2.5 text-sm font-medium rounded-xl transition flex items-center gap-2 shadow-sm hover:shadow-md ${
                dateFilter
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-100'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
              }`}
            >
              <FiCalendar className={dateFilter ? "text-blue-100" : "text-gray-400"} size={14} /> {dateFilter ? new Date(dateFilter).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Date'}
            </button>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="absolute opacity-0 pointer-events-none"
              style={{ width: 0, height: 0 }}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 ml-auto">
            <button 
              onClick={fetchExperiences} 
              className="h-10 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm hover:shadow-md flex items-center gap-2"
            >
              <FiRefreshCw size={14} /> <span className="hidden xs:inline">Refresh</span>
            </button>

            {/* Clear Filters Button */}
            {(searchQuery || roleFilter || dateFilter) && (
              <button
                onClick={resetFilters}
                className="h-10 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm hover:shadow-md"
              >
                <FiX size={14} /> Clear
              </button>
            )}
          </div>
        </div>
      </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <FiX className="text-red-500 text-lg" />
          <span className="text-sm font-medium text-red-700">{error}</span>
        </div>
      )}

      {/* Table Section */}
      <div className="emp-dash__card mb-6">
        {loading ? (
          <div className="py-10 text-center">
            <div className="flex flex-col items-center justify-center gap-3">
              <div className="emp-dash__spinner"></div>
              <span className="text-sm font-medium text-gray-500">Syncing employee records...</span>
            </div>
          </div>
        ) : filteredCandidates.length > 0 ? (
          <>
            {/* Desktop Table - Hidden on mobile */}
            <div className="hidden md:block emp-dash__table-wrap overflow-x-auto w-full">
              <table className="emp-dash__table min-w-[800px] w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-50 to-blue-50">
                    <th className="px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 whitespace-nowrap text-left">Employee Name</th>
                    <th className="px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 whitespace-nowrap text-center">Latest Role</th>
                    <th className="px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 whitespace-nowrap text-center">Contact</th>
                    <th className="px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 whitespace-nowrap text-center">Roles Logged</th>
                    <th className="px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 whitespace-nowrap text-center">Status</th>
                    <th className="px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 whitespace-nowrap text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 transition-all duration-200 group">
                      <td className="px-4 py-4 font-medium text-left">
                        <div className="text-gray-900 whitespace-nowrap font-semibold group-hover:text-blue-600 transition-colors">{item.candidate?.name || "Unknown"}</div>
                        <div className="text-[11px] text-gray-500 font-medium mt-0.5">{item.lastUpdated ? new Date(item.lastUpdated).toLocaleDateString() : "No updates"}</div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="inline-block px-3 py-1.5 bg-blue-50 text-blue-600 text-[11px] font-semibold rounded-full border border-blue-100 shadow-sm">
                          {item.latestRole}
                        </span>
                        <div className="text-[11px] text-gray-400 mt-1">{item.latestCompany}</div>
                      </td>
                      <td className="px-4 py-4 text-center text-gray-600">
                        <div className="flex flex-col items-center gap-0.5">
                          <div className="flex items-center gap-1">
                            <div className="text-[11px] font-medium">{item.candidate?.phone || "N/A"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 font-bold text-xs border border-indigo-100 shadow-sm">
                          {item.experiences.length}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="px-3 py-1.5 rounded-full text-[11px] bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 font-semibold border border-emerald-200 shadow-sm">
                          Verified
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleOpenModal(item)}
                            className="px-4 py-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-indigo-100 flex items-center justify-center gap-1.5 text-xs font-bold hover:scale-105"
                          >
                            <FiEye className="text-xs" /> View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards - Visible on mobile only */}
            <div className="md:hidden p-4">
              {currentItems.map((item, idx) => (
                <EmployeeCard 
                  key={idx} 
                  item={item} 
                  handleOpenModal={handleOpenModal} 
                />
              ))}
            </div>
            
            {/* Pagination */}
            {filteredCandidates.length > 0 && (
              <div className="flex flex-col items-center justify-between px-6 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50/30 sm:flex-row">
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                  <span>Showing</span>
                  <span className="font-bold text-gray-900">
                    {indexOfFirstItem + 1}
                  </span>
                  <span>to</span>
                  <span className="font-bold text-gray-900">
                    {Math.min(indexOfLastItem, filteredCandidates.length)}
                  </span>
                  <span>of</span>
                  <span className="font-bold text-gray-900">
                    {filteredCandidates.length}
                  </span>
                  <span>results</span>

                  <select
                    value={pagination.limit}
                    onChange={(e) => {
                      const newLimit = Number(e.target.value);
                      handleItemsPerPageChange(newLimit);
                    }}
                    className="p-2 ml-2 text-sm border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className="flex items-center gap-2 mt-4 sm:mt-0">
                  <button
                    onClick={handlePrevPage}
                    disabled={pagination.currentPage === 1}
                    className={`px-5 py-2.5 border rounded-xl text-sm font-semibold transition-all shadow-sm ${
                      pagination.currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md"
                    }`}
                  >
                    ← Prev
                  </button>

                  <div className="flex items-center gap-1">
                    {getPageNumbers().map((page, index) => (
                      <button
                        key={index}
                        onClick={() => typeof page === 'number' ? handlePageClick(page) : null}
                        disabled={page === "..."}
                        className={`w-10 h-10 rounded-xl text-sm font-bold transition-all shadow-sm ${
                          page === "..."
                            ? "text-gray-500 cursor-default"
                            : pagination.currentPage === page
                            ? "bg-gradient-to-r from-indigo-500 to-blue-600 text-white shadow-lg shadow-indigo-200 hover:shadow-xl hover:scale-105"
                            : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className={`px-5 py-2.5 border rounded-xl text-sm font-semibold transition-all shadow-sm ${
                      pagination.currentPage === pagination.totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md"
                    }`}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-24">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-gray-100">
              <FiBriefcase className="text-3xl text-gray-300" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">No journey records found</h2>
            <p className="text-sm text-gray-500 mb-6">Try refined search parameters</p>
            {(searchQuery || roleFilter || dateFilter) && (
              <button
                onClick={resetFilters}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-blue-200 hover:shadow-xl hover:scale-105 transition-all duration-200"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Employee Details Modal */}
      {isModalOpen && selectedCandidate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-gray-900/40 backdrop-blur-[2px] animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-3xl rounded-2xl sm:rounded-2xl shadow-2xl overflow-hidden relative animate-in slide-in-from-bottom-4 duration-300 border border-gray-100 max-h-[95vh] flex flex-col">

            {/* Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 sm:top-5 sm:right-5 p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all z-10 hover:scale-110"
            >
              <FiX className="text-lg" />
            </button>

            {/* Header */}
            <div className="px-4 sm:px-8 pt-6 sm:pt-8 pb-4 sm:pb-5 flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-gray-200">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  {selectedCandidate.candidate?.name || "Unknown"}
                </h2>
                <p className="text-[11px] font-bold uppercase tracking-widest mt-1 text-blue-600">
                  {selectedCandidate.latestRole || "Employee"}
                </p>
              </div>

              <span className="px-4 py-1.5 text-xs font-semibold rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 border border-emerald-200 shadow-sm">
                Verified
              </span>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-8 pt-2 pb-4 no-scrollbar">

              {/* Basic Info */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
                  <DetailItem label="Email" value={selectedCandidate.candidate?.email} />
                  <DetailItem label="Phone" value={selectedCandidate.candidate?.phone} />
                  <DetailItem label="Latest Company" value={selectedCandidate.latestCompany} />
                  <DetailItem label="Roles Logged" value={selectedCandidate.experiences.length} />
                </div>
              </div>

              {/* Experience History */}
              <div className="mt-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">
                  Professional History
                </h3>
                <div className="space-y-4">
                  {selectedCandidate.experiences.map((exp, eIdx) => (
                    <div key={eIdx} className="p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="flex flex-wrap items-start justify-between mb-2 gap-1">
                        <div>
                          <h4 className="text-sm font-bold text-gray-800">{exp.role || "Unknown Role"}</h4>
                          <p className="text-xs text-gray-600">{exp.companyName || "Unknown Company"}</p>
                        </div>
                        <span className="text-[10px] text-gray-400">#{selectedCandidate.experiences.length - eIdx}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-xs">
                        <div>
                          <span className="text-gray-500">Duration:</span>
                          <span className="ml-2 font-medium text-gray-700">{formatDate(exp.startDate)} - {formatDate(exp.endDate)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Location:</span>
                          <span className="ml-2 font-medium text-gray-700">{exp.location || "N/A"}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">CTC:</span>
                          <span className="ml-2 font-medium text-gray-700">{exp.salary || "N/A"}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {exp.offerLetter ? (
                          <a
                            href={getFileUrl(exp.offerLetter)}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg text-[10px] font-medium transition-all"
                          >
                            <FiFileText className="text-xs" /> Offer Letter
                          </a>
                        ) : (
                          <span className="px-3 py-1.5 bg-gray-100 text-gray-400 rounded-lg text-[10px]">No Letter</span>
                        )}
                        {exp.payslip ? (
                          <a
                            href={getFileUrl(exp.payslip)}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg text-[10px] font-medium transition-all"
                          >
                            <FiFileText className="text-xs" /> Payslip
                          </a>
                        ) : (
                          <span className="px-3 py-1.5 bg-gray-100 text-gray-400 rounded-lg text-[10px]">No Payslip</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 sm:px-6 py-4 sm:py-5 border-t border-gray-200 flex justify-end bg-gradient-to-r from-gray-50 to-blue-50">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-2.5 bg-gradient-to-r from-gray-800 to-gray-900 text-white text-xs font-semibold rounded-xl hover:from-gray-900 hover:to-black transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </main>
    </div>
  );
};

export default EmployeeJourney;